import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Pill } from '@els/els-react--pill';

import './RemovablePill.scss';

/**
 * Thin wrapper around Leyden's Pill for removable "chips".
 *
 * Leyden builds the close button's accessible name from BOTH the visible pill
 * children and the icon's "Remove <children>" text, so a screen reader
 * announces the term twice (e.g. "Aspirin Remove Aspirin"). The Pill exposes no
 * prop to name its close button, so we set a single explicit aria-label on it
 * ("Remove <label>") — announced once, and still containing the visible label
 * so WCAG 2.5.3 (Label in Name) holds.
 *
 * The wrapper element uses `display: contents` so it adds no box of its own and
 * the Pill keeps participating in the parent layout exactly as before.
 */
function RemovablePill({ label, onRemove, className, children, 'data-e2e': dataE2e }) {
  const wrapperRef = useRef(null);

  useEffect(() => {
    const button = wrapperRef.current?.querySelector('.c-els-pill__close-button');

    if (button) {
      button.setAttribute('aria-label', `Remove ${label}`);
    }
  }, [label, children]);

  return (
    <span ref={wrapperRef} className="removablePill" data-e2e={dataE2e}>
      <Pill className={className} onClose={onRemove}>
        {children}
      </Pill>
    </span>
  );
}

RemovablePill.propTypes = {
  /** Plain-text label used to build the close button's accessible name. */
  label: PropTypes.string.isRequired,
  /** Called when the pill's close button is activated. */
  onRemove: PropTypes.func.isRequired,
  className: PropTypes.string,
  /** Visible pill content. */
  children: PropTypes.node,
  'data-e2e': PropTypes.string,
};

export default RemovablePill;
